// src/components/CostImpactCalculator.js

import { useMemo } from 'react';
import { DollarSign, TrendingDown, Package, AlertTriangle } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useTheme } from '../context/ThemeContext';

const formatCurrency = (value) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
};

function StatBox({ label, value, subtext, color, icon: Icon, colors }) {
  return (
    <div style={{ background: colors.bgElevated, borderRadius: '12px', padding: '1.25rem', display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
      <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={20} color={color} />
      </div>
      <div>
        <div style={{ fontSize: '1.5rem', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: colors.textPrimary, marginBottom: '0.25rem' }}>{value}</div>
        <div style={{ fontSize: '0.8rem', color: colors.textSecondary }}>{label}</div>
        {subtext && <div style={{ fontSize: '0.7rem', color: colors.textMuted, marginTop: '0.25rem' }}>{subtext}</div>}
      </div>
    </div>
  );
}

function CostImpactCalculator({ shipments, excursions }) {
  const { colors } = useTheme();
  
  const totalValueAtRisk = useMemo(() => excursions.reduce((sum, e) => sum + e.valueAtRisk, 0), [excursions]);
  const actualLosses = useMemo(() => excursions.filter(e => e.productLost).reduce((sum, e) => sum + e.valueAtRisk, 0), [excursions]);
  const potentialSavings = useMemo(() => totalValueAtRisk - actualLosses, [totalValueAtRisk, actualLosses]);
  
  const costBySeverity = useMemo(() => {
    const bySeverity = { Critical: 0, Major: 0, Minor: 0 };
    excursions.forEach(e => bySeverity[e.severity] += e.valueAtRisk);
    return [
      { name: 'Critical', value: bySeverity.Critical, color: colors.danger },
      { name: 'Major', value: bySeverity.Major, color: colors.warning },
      { name: 'Minor', value: bySeverity.Minor, color: colors.info },
    ];
  }, [excursions, colors]);
  
  const costByRootCause = useMemo(() => {
    const byCause = {};
    excursions.forEach(e => byCause[e.rootCause] = (byCause[e.rootCause] || 0) + e.valueAtRisk);
    return Object.entries(byCause).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 5);
  }, [excursions]);
  
  const costTrend = useMemo(() => {
    const byWeek = {};
    excursions.forEach(e => {
      const date = new Date(e.startTime);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      const weekKey = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      byWeek[weekKey] = (byWeek[weekKey] || 0) + e.valueAtRisk;
    });
    return Object.entries(byWeek).map(([week, value]) => ({ week, value })).slice(-8);
  }, [excursions]);
  
  const unitsAffected = useMemo(() => {
    let total = 0;
    excursions.forEach(e => {
      const shipment = shipments.find(s => s.id === e.shipmentId);
      if (shipment) total += shipment.units;
    });
    return total;
  }, [excursions, shipments]);

  return (
    <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: '16px', overflow: 'hidden' }}>
      <div style={{ padding: '1.25rem 1.5rem', borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', gap: '0.5rem', background: colors.bgSecondary }}>
        <DollarSign size={20} color={colors.danger} />
        <span style={{ fontSize: '1rem', fontWeight: 600, color: colors.textPrimary }}>Cost Impact Analysis</span>
      </div>
      
      <div style={{ padding: '1.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <StatBox icon={AlertTriangle} label="Total Value at Risk" value={formatCurrency(totalValueAtRisk)} subtext={`${excursions.length} excursion events`} color={colors.danger} colors={colors} />
          <StatBox icon={TrendingDown} label="Actual Losses" value={formatCurrency(actualLosses)} subtext="Product destroyed/rejected" color={colors.warning} colors={colors} />
          <StatBox icon={DollarSign} label="Saved (Recovered)" value={formatCurrency(potentialSavings)} subtext="Product still usable" color={colors.success} colors={colors} />
          <StatBox icon={Package} label="Units Affected" value={unitsAffected.toLocaleString()} subtext="Across all excursions" color={colors.purple} colors={colors} />
        </div>
        
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1rem' }}>Weekly Cost Trend</div>
          <div style={{ height: '200px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={costTrend}>
                <defs>
                  <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={colors.danger} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={colors.danger} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="week" stroke={colors.chartText} fontSize={11} tickLine={false} axisLine={{ stroke: colors.chartGrid }} />
                <YAxis stroke={colors.chartText} fontSize={11} tickLine={false} axisLine={{ stroke: colors.chartGrid }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} />
                <Tooltip contentStyle={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: '8px' }} />
                <Area type="monotone" dataKey="value" stroke={colors.danger} fill="url(#costGradient)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          <div>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1rem' }}>Cost by Severity</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ width: '100px', height: '100px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart><Pie data={costBySeverity} cx="50%" cy="50%" innerRadius={30} outerRadius={45} dataKey="value" strokeWidth={0}>
                    {costBySeverity.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                  </Pie></PieChart>
                </ResponsiveContainer>
              </div>
              <div style={{ flex: 1 }}>
                {costBySeverity.map(item => (
                  <div key={item.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: item.color }} />
                      <span style={{ fontSize: '0.75rem', color: colors.textSecondary }}>{item.name}</span>
                    </div>
                    <span style={{ fontSize: '0.8rem', fontWeight: 600, fontFamily: "'JetBrains Mono', monospace", color: colors.textPrimary }}>{formatCurrency(item.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1rem' }}>Top Root Causes</div>
            {costByRootCause.map((cause, index) => (
              <div key={cause.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 0', borderBottom: `1px solid ${colors.border}` }}>
                <span style={{ fontSize: '0.85rem', color: colors.textSecondary }}>{index + 1}. {cause.name}</span>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, fontFamily: "'JetBrains Mono', monospace", color: colors.textPrimary }}>{formatCurrency(cause.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CostImpactCalculator;